import React, {useContext, useEffect, useState} from 'react';
import axios from 'axios';
import ReactApexChart from 'react-apexcharts';
import {Link, useNavigate} from 'react-router-dom';
import withAuth from '../../auth';
import {API_URL} from '../../constants';
import {AuthContext} from '../AuthContext';
import AppVersionStatistics from './AppVersionStatistics';

const platforms = [
    {key: 'android', label: 'Android', color: '#218739', icon: 'android'},
    {key: 'ios', label: 'iOS', color: '#3867d6', icon: 'apple'},
];

const audiences = [
    {key: 'users', title: 'Users (customers)', label: 'users'},
    {key: 'drivers', title: 'Drivers', label: 'drivers'},
];

const formatCount = (value) => Math.round(value).toLocaleString();

const PlatformChart = ({audience, counts}) => {
    const series = platforms.map(({key}) => counts[key]);
    const hasData = series.some((count) => count > 0);
    const options = {
        chart: {
            type: 'bar',
            toolbar: {show: false},
            fontFamily: 'inherit',
        },
        colors: platforms.map(({color}) => color),
        plotOptions: {
            bar: {distributed: true, columnWidth: '45%', borderRadius: 4},
        },
        dataLabels: {enabled: true, formatter: formatCount},
        xaxis: {categories: platforms.map(({label}) => label)},
        yaxis: {
            min: 0,
            forceNiceScale: true,
            decimalsInFloat: 0,
            title: {text: `Unique ${audience.label}`},
            labels: {formatter: formatCount},
        },
        legend: {show: false},
        tooltip: {y: {formatter: (value) => `${formatCount(value)} ${audience.label}`}},
    };

    return (
        <section className="card h-100" aria-labelledby={`${audience.key}-platform-title`}>
            <div className="card-body">
                <h3 id={`${audience.key}-platform-title`} className="h5 mb-4">{audience.title}</h3>
                <dl className="row text-center mb-0">
                    {platforms.map((platform) => (
                        <div className="col-6" key={platform.key}>
                            <dt className="text-muted font-weight-normal">
                                <i className={`fa fa-${platform.icon} mr-2`} style={{color: platform.color}} aria-hidden="true"/>
                                {platform.label} {audience.label}
                            </dt>
                            <dd className="h3 mt-2" style={{color: platform.color}}>
                                {formatCount(counts[platform.key])}
                            </dd>
                        </div>
                    ))}
                </dl>
                {hasData ? (
                    <div aria-label={`${audience.title} by platform`}>
                        <ReactApexChart
                            options={options}
                            series={[{name: `Unique ${audience.label}`, data: series}]}
                            type="bar"
                            height={320}
                        />
                    </div>
                ) : (
                    <p className="text-center text-muted py-5 mb-0">
                        No Android or iOS devices linked to {audience.label} yet.
                    </p>
                )}
            </div>
        </section>
    );
};

export const DeviceStatistics = () => {
    const {token, setToken} = useContext(AuthContext);
    const navigate = useNavigate();
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [requestNumber, setRequestNumber] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        let active = true;

        const fetchStatistics = async () => {
            setLoading(true);
            setError(null);
            setStatistics(null);

            try {
                const response = await axios.get(`${API_URL}/api/admin/devices/statistics`, {
                    headers: {Authorization: `Bearer ${token}`},
                    signal: controller.signal,
                });
                if (!active) return;

                const data = response.data.data;
                const validCounts = audiences.every(({key}) => platforms.every((platform) => {
                    const count = data?.[key]?.[platform.key];
                    return Number.isSafeInteger(count) && count >= 0;
                }));
                const validVersions = audiences.every(({key}) => {
                    const versions = data?.app_versions?.[key];
                    return Array.isArray(versions) && versions.every((version) => (
                        version !== null
                        && (version.version === null || (typeof version.version === 'string' && version.version.trim() !== ''))
                        && platforms.every((platform) => Number.isSafeInteger(version[platform.key]) && version[platform.key] >= 0)
                    ));
                });
                if (!validCounts || !validVersions) throw new Error('Invalid device statistics response');

                setStatistics(data);
            } catch (requestError) {
                if (!active) return;

                if (requestError.response?.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/', {replace: true});
                } else {
                    setError(requestError.response?.status === 403
                        ? 'You do not have permission to view device statistics.'
                        : 'Unable to load device statistics. Please try again.');
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchStatistics();

        return () => {
            active = false;
            controller.abort();
        };
    }, [token, setToken, navigate, requestNumber]);

    return (
        <div className="container-fluid mt-3 mb-4">
            <h2 className="text-center">Device Statistics</h2>
            <p className="text-center text-muted">Platform and app version usage across users and drivers.</p>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Link className="btn btn-outline-secondary btn-sm" to="/devices">
                    <i className="fa fa-list mr-2" aria-hidden="true"/>Device List
                </Link>
                <button
                    className="btn btn-outline-primary btn-sm"
                    type="button"
                    onClick={() => setRequestNumber((value) => value + 1)}
                    disabled={loading}
                >
                    <i className="fa fa-refresh mr-2" aria-hidden="true"/>Refresh
                </button>
            </div>
            {loading ? (
                <div className="d-flex justify-content-center py-5" role="status">
                    <div className="spinner-border" aria-hidden="true"/>
                    <span className="sr-only">Loading device statistics...</span>
                </div>
            ) : error ? (
                <div className="alert alert-danger" role="alert">
                    <p className="mb-2">{error}</p>
                    <button
                        className="btn btn-outline-danger btn-sm"
                        type="button"
                        onClick={() => setRequestNumber((value) => value + 1)}
                    >
                        Try again
                    </button>
                </div>
            ) : statistics && (
                <>
                    <p className="text-muted small">
                        Based on registered devices. Each account is counted once per platform.
                        {' '}Accounts with both Android and iOS devices appear in both counts.
                        {' '}Devices without a linked account or a recognized mobile platform are excluded.
                    </p>
                    <div className="row">
                        {audiences.map((audience) => (
                            <div className="col-12 col-lg-6 mb-4" key={audience.key}>
                                <PlatformChart audience={audience} counts={statistics[audience.key]}/>
                            </div>
                        ))}
                    </div>
                    <h3 className="h4">App version statistics</h3>
                    <p className="text-muted small">
                        Each account is counted once per app version and platform.
                        {' '}Accounts using multiple versions appear in each version they use, so version counts may exceed platform totals.
                        {' '}Missing versions are shown as Unknown. Versions are listed newest first.
                    </p>
                    <div className="row">
                        {audiences.map((audience) => (
                            <div className="col-12 col-lg-6 mb-4" key={audience.key}>
                                <AppVersionStatistics audience={audience} versions={statistics.app_versions[audience.key]}/>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default withAuth(DeviceStatistics);
